import React, { useEffect, useMemo, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { BriefcaseBusiness, CircleDollarSign, Loader2, ShieldCheck } from "lucide-react"
import { useFetchSubscriptions } from "../../hooks/DataFetching/useFetchSubscriptions"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../config/featureFlags"
import BTCConverter from "../BTCConverter"
import "../../styles/components/DashboardRoles.css"

const formatDate = (value) => {
  if (!value) {
    return "Ongoing"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return "Ongoing"
  }

  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const normalizeUserId = (value) => String(value || "").replace(/\|/g, "")

const DashboardRoles = () => {
  const { user, getAccessTokenSilently } = useAuth0()
  const { subscriptions, loading: membershipsLoading } = useFetchSubscriptions()
  const apiUrl = process.env.REACT_APP_API_URL
  const jobsPayrollEnabled = isFeatureEnabled(FEATURE_FLAGS.jobsPayroll)
  const [positions, setPositions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const userId = normalizeUserId(user?.sub)
  const membershipGroups = useMemo(() => (
    (subscriptions || []).map((subscription) => ({
      groupId: subscription.groupId,
      groupName: subscription.groupName || `Group ${subscription.groupId}`,
    }))
  ), [subscriptions])

  useEffect(() => {
    let isMounted = true

    const fetchAssignedPositions = async () => {
      if (!userId || membershipsLoading || !jobsPayrollEnabled || !membershipGroups.length) {
        setPositions([])
        setError("")
        setLoading(false)
        return
      }

      setLoading(true)
      setError("")

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        })
        const headers = {
          Authorization: `Bearer ${token}`,
        }
        const responses = await Promise.all(
          membershipGroups.map(async (subscription) => {
            const response = await fetch(`${apiUrl}/api/Positions/group/${subscription.groupId}`, { headers })
            if (!response.ok) {
              throw new Error("Could not load assigned roles.")
            }
            const data = await response.json()
            return (Array.isArray(data) ? data : []).map((position) => ({
              ...position,
              groupName: subscription.groupName || `Group ${subscription.groupId}`,
            }))
          }),
        )

        if (isMounted) {
          setPositions(responses.flat())
        }
      } catch (err) {
        console.error("Error fetching dashboard roles:", err)
        if (isMounted) {
          setPositions([])
          setError("Assigned roles are unavailable right now.")
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchAssignedPositions()

    return () => {
      isMounted = false
    }
  }, [apiUrl, getAccessTokenSilently, jobsPayrollEnabled, membershipGroups, membershipsLoading, userId])

  const assignedRoles = useMemo(() => positions.filter((position) => (
    normalizeUserId(position.memberId) === userId &&
    String(position.positionStatus || "").toLowerCase() === "filled"
  )), [positions, userId])

  const totalWeeklyBtc = assignedRoles.reduce((sum, role) => sum + Number(role.weeklyCompensation || 0), 0)

  return (
    <section className="dashboard-roles-real" aria-label="Positions, roles, and compensation">
      <div className="dashboard-roles-summary">
        <div>
          <span className="dashboard-roles-kicker">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Assigned roles
          </span>
          <h3>Positions, Roles & Compensation</h3>
        </div>
        <div className="dashboard-role-total">
          <CircleDollarSign className="h-4 w-4" aria-hidden="true" />
          <span>Weekly BTC</span>
          <strong>
            <BTCConverter btcAmount={totalWeeklyBtc} />
          </strong>
        </div>
      </div>

      {loading || membershipsLoading ? (
        <div className="dashboard-role-state">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading assigned roles...
        </div>
      ) : error ? (
        <div className="dashboard-role-state is-warning">{error}</div>
      ) : assignedRoles.length === 0 ? (
        <div className="dashboard-role-state">
          No active role assignments found yet. Filled group positions assigned to you will appear here.
        </div>
      ) : (
        <div className="dashboard-role-list">
          {assignedRoles.map((role) => (
            <article key={`${role.groupId}-${role.id}`} className="dashboard-role-row">
              <div className="dashboard-role-icon">
                <BriefcaseBusiness className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="dashboard-role-main">
                <h4>{role.title}</h4>
                <p>{role.groupName}</p>
                {role.positionDuties && <span>{role.positionDuties}</span>}
              </div>
              <div className="dashboard-role-meta">
                {role.weeklyCompensation ? <BTCConverter btcAmount={Number(role.weeklyCompensation)} /> : "Volunteer"}
                <small>{formatDate(role.hireDate)} - {formatDate(role.endDate)}</small>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}

export default DashboardRoles
